import type { PaginatedResponse } from '@cpce/shared';
import { QueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { CommiteeIdResolutionStatus, useCommiteeIdResolutionFromUrl } from '@/utils/router';
import { apiGet } from './api.utils';

const MAX_RETRIES = 3;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx answer (unknown id, validation) will not change on retry: fail at once.
      retry: (failureCount, error) =>
        failureCount < MAX_RETRIES &&
        !(isAxiosError(error) && (error.response?.status ?? 500) < 500),
    },
  },
});

export type QueryKey = (string | number | object)[];

interface QueryOptions<T, R = T> {
  queryKey: QueryKey;
  url: string;
  transform?: (data: T) => R;
  params?: object;
  enabled?: boolean;
  staleTime?: number;
  /** Off for a query whose endpoint calls the billetterie: a page left open would keep asking. */
  refetchOnFocusOrReconnect?: boolean;
  /** Polling period in ms, read from the last answer; false stops it. */
  refetchInterval?: (data: T | undefined) => number | false;
}

interface QueryResult<T, R = T> {
  data?: R;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

// Toasts a query/transform error and rethrows so react-query still enters its error state. Module-scoped
// (it closes over no component state) so a stable `select` callback can reference it.
function reportQueryError(error: unknown): never {
  let message = 'Erreur inconnue';
  if (isAxiosError(error) && error.response?.data?.message) {
    message = error.response.data.message;
  } else if (error instanceof Error) {
    message = error.message;
  }
  if (!(isAxiosError(error) && error.response?.status === 404)) {
    toast.error(`Une erreur est survenue: ${message}`, { id: message });
  }
  throw error;
}

export function query<T, R = T>(options: QueryOptions<T, R>): QueryResult<T, R> {
  const {
    queryKey,
    url,
    transform,
    params,
    enabled,
    staleTime,
    refetchOnFocusOrReconnect,
    refetchInterval,
  } = options;

  // Keep `select` referentially stable so react-query memoizes its result across renders. An unstable select
  // (a fresh closure each render, as an inline `select: (data) => ...` would be) makes react-query re-run
  // `transform` on every render; a transform that builds new objects — Decimal/Date instances, which
  // react-query's structural sharing can't dedupe — then hands back a brand-new array on every render. List
  // tables that push their filtered rows up via `onFilteredDataChange` compare those rows by reference, so a
  // new reference each render loops setState → render → setState forever ("Maximum update depth exceeded").
  // The ref lets the stable callback always call the latest (pure) transform without changing identity.
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const select = useCallback((data: T): R => {
    try {
      const currentTransform = transformRef.current;
      return currentTransform ? currentTransform(data) : (data as unknown as R);
    } catch (error) {
      reportQueryError(error);
    }
  }, []);

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await apiGet<T>(url, params);
      } catch (error) {
        reportQueryError(error);
      }
    },
    select,
    enabled,
    staleTime,
    refetchOnWindowFocus: refetchOnFocusOrReconnect,
    refetchOnReconnect: refetchOnFocusOrReconnect,
    refetchInterval:
      refetchInterval &&
      ((current: { state: { data: T | undefined } }) => refetchInterval(current.state.data)),
  });

  return {
    data: queryResult.data as R | undefined,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
  };
}

export function isNotFoundError(error: Error | null): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

export function queryKeysBuilder(base: QueryKey): (...keys: QueryKey) => QueryKey {
  return (...keys) => [...base, ...keys];
}

export function commiteeQuery<T, R = T>(options: QueryOptions<T, R>): QueryResult<T, R> {
  const commiteeIdResolution = useCommiteeIdResolutionFromUrl();
  const requiresCommiteeId = options.url.includes(':commiteeId');
  const [computedUrl, computedQueryKey] = useMemo(() => {
    if (requiresCommiteeId) {
      if (commiteeIdResolution.status === CommiteeIdResolutionStatus.LOADING)
        return [options.url, [...options.queryKey, 'pending-commitee-id']];

      if (commiteeIdResolution.status === CommiteeIdResolutionStatus.NOT_FOUND)
        throw new Error('Commitee ID is required');

      return [
        options.url.replace(':commiteeId', commiteeIdResolution.commiteeId.toString()),
        [...options.queryKey, commiteeIdResolution.commiteeId],
      ];
    }
    return [options.url, options.queryKey];
  }, [requiresCommiteeId, options.url, options.queryKey, commiteeIdResolution]);

  return query<T, R>({
    ...options,
    queryKey: computedQueryKey ?? [],
    url: computedUrl,
    enabled:
      (requiresCommiteeId ? commiteeIdResolution.status === 'ready' : true) &&
      (options.enabled ?? true),
  });
}

const DEFAULT_PAGE_SIZE = 50;

export interface InfiniteQueryResult<T> {
  data: T[];
  total: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function commiteeInfiniteQuery<T>(options: {
  queryKey: QueryKey;
  url: string;
  params?: object;
  pageSize?: number;
  enabled?: boolean;
}): InfiniteQueryResult<T> {
  const { queryKey, url, params, pageSize = DEFAULT_PAGE_SIZE, enabled } = options;
  const commiteeIdResolution = useCommiteeIdResolutionFromUrl();
  const requiresCommiteeId = url.includes(':commiteeId');

  const [computedUrl, computedQueryKey] = useMemo(() => {
    if (requiresCommiteeId) {
      if (commiteeIdResolution.status === CommiteeIdResolutionStatus.LOADING)
        return [url, [...queryKey, 'pending-commitee-id']];

      if (commiteeIdResolution.status === CommiteeIdResolutionStatus.NOT_FOUND)
        throw new Error('Commitee ID is required');

      return [
        url.replace(':commiteeId', commiteeIdResolution.commiteeId.toString()),
        [...queryKey, commiteeIdResolution.commiteeId],
      ];
    }
    return [url, queryKey];
  }, [requiresCommiteeId, url, queryKey, commiteeIdResolution]);

  const result = useInfiniteQuery({
    queryKey: computedQueryKey,
    queryFn: async ({ pageParam }) => {
      try {
        return await apiGet<PaginatedResponse<T>>(computedUrl, {
          ...params,
          page: pageParam,
          pageSize,
        });
      } catch (error) {
        reportQueryError(error);
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.pageSize);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    enabled:
      (requiresCommiteeId ? commiteeIdResolution.status === 'ready' : true) && (enabled ?? true),
  });

  const data = useMemo(() => result.data?.pages.flatMap((p) => p.data) ?? [], [result.data]);
  const lastPage = result.data?.pages[result.data.pages.length - 1];

  return {
    data,
    total: lastPage?.total ?? 0,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    isLoading: result.isLoading,
    isError: result.isError,
  };
}
